// AnimatedTabs — segmented control com indicador que desliza animado
// quando o aluno troca de aba. Usa medições do DOM (ref do botão ativo)
// pra que botões de larguras diferentes funcionem (ex: "Plano da plataforma"
// é maior que "Cartões e cobranças").
//
// Por que não CSS-only com flex-1 + transform: translateX(idx * 100%)?
// Porque os botões variam de largura conforme o label/idioma. Medir é o
// jeito robusto pra UX de slide ficar pixel-perfect.
//
// Uso típico:
//   <AnimatedTabs
//     items={[{ id: "plano", label: "Plano", icon: BadgeDollarSign }, ...]}
//     value={tab}
//     onChange={setTab}
//   />
//
// Aceita também `size="sm"` pros segmenteds inline (Tema/Fonte) e
// `ariaLabel` pro a11y.

import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { ComponentType, ComponentChildren } from "preact";
import { cn } from "@app/ui/lib/utils";

export interface AnimatedTabItem<T extends string> {
  id: T;
  label: ComponentChildren;
  /** Versão curta do label pra telas estreitas. Se ausente, usa `label`. */
  shortLabel?: ComponentChildren;
  // Aceitamos qualquer ComponentType — em prática são ícones lucide-react
  // (LucideIcon = ForwardRefExoticComponent) que não casa direto com
  // ComponentType<{className?: string}> sem `any`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: ComponentType<any>;
  /** Quando true, esconde o botão (placeholder pra reorder dinâmico). */
  hidden?: boolean;
  /** Quando true, botão fica visível mas não-clicável (cinza-claro). Útil
   *  pra opções condicionalmente disponíveis (ex: "Conta conectada" só
   *  habilita após o OAuth do Google). */
  disabled?: boolean;
  /** Tooltip opcional. */
  title?: string;
}

interface AnimatedTabsProps<T extends string> {
  items: AnimatedTabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  /** `default` (md, gap maior, ideal pra pages) ou `sm` (compacto, ideal
   *  pra controles inline tipo Tema/Fonte). */
  size?: "default" | "sm";
  /** `subtle` (default) usa indicador accent — discreto, bom pra controles
   *  secundários. `strong` força bg-primary + texto invertido — usar quando
   *  o segmented control é a ação principal do contexto e o usuário precisa
   *  reconhecer rapidamente qual aba está ativa (especialmente em dark mode,
   *  onde accent some). */
  variant?: "subtle" | "strong";
  ariaLabel?: string;
  className?: string;
}

export function AnimatedTabs<T extends string>({
  items,
  value,
  onChange,
  size = "default",
  variant = "subtle",
  ariaLabel,
  className
}: AnimatedTabsProps<T>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRefs = useRef<Map<T, HTMLButtonElement>>(new Map());
  const [indicator, setIndicator] = useState<{ left: number; width: number; ready: boolean }>(
    { left: 0, width: 0, ready: false }
  );

  function measure() {
    const container = containerRef.current;
    const activeBtn = buttonRefs.current.get(value);
    if (!container || !activeBtn) return;
    const cRect = container.getBoundingClientRect();
    const bRect = activeBtn.getBoundingClientRect();
    setIndicator({
      left: bRect.left - cRect.left,
      width: bRect.width,
      ready: true
    });
  }

  // useLayoutEffect pra evitar flash do indicador no canto antes da medição.
  useLayoutEffect(() => {
    measure();
  }, [value]);

  // Remede quando o container muda de tamanho (orientation change, font load,
  // sidebar toggle, etc). ResizeObserver pega tudo isso.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(container);
    buttonRefs.current.forEach((b) => ro.observe(b));
    return () => ro.disconnect();
  }, []);

  // size=sm: parent fica em bg-muted/40 pra dar contraste contra o pílulo
  // violeta (bg-primary). Padrão segmented control com brand color.
  // Antes tentei bg-background contra bg-muted — em dark o pílulo ficou
  // MAIS escuro que o parent (parecia um buraco). Bug Luís 2026-05-25.
  const containerCls =
    size === "sm"
      ? "relative inline-flex items-center gap-0 rounded-md border border-border bg-muted/40 p-1 text-[11px]"
      : "relative inline-flex flex-wrap items-center gap-0 rounded-lg border border-border bg-card/40 p-1";

  // Indicador SEMPRE em bg-primary (roxo da brand). Antes tentei bg-card
  // / bg-background pro subtle e ficava invisível ou virava buraco no dark.
  // Decidido com Luís 2026-05-25 ("Não deveria ser o padrão roxo?") —
  // mantém o `variant` como API só pra diferenciar texto/sombra/borda no
  // futuro se precisar de ainda mais ênfase, mas a cor base é roxo nos dois.
  const indicatorBgCls = "bg-primary";
  // Strong: levanta o pílulo c/ sombra discreta. Subtle: shadow-sm padrão.
  const indicatorShadowCls =
    variant === "strong" ? "shadow-md" : "shadow-sm";
  // top/bottom 0 (inset full no container que já tem p-1 = 4px). Sem
  // overflow, dá um pílulo que ocupa quase toda a altura do segmented.
  const indicatorCls =
    size === "sm"
      ? `absolute top-0 bottom-0 rounded ${indicatorBgCls} ${indicatorShadowCls} transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]`
      : `absolute top-1 bottom-1 rounded-md ${indicatorBgCls} ${indicatorShadowCls} transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]`;

  const buttonBaseCls =
    size === "sm"
      ? "relative z-10 flex-1 rounded px-2 py-1 font-medium transition-colors"
      : "relative z-10 inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold transition-colors";

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={ariaLabel}
      class={cn(containerCls, className)}
    >
      {/* Indicador que desliza atrás dos botões. Escondido até a primeira
          medição pra não piscar do canto. */}
      <div
        aria-hidden="true"
        class={indicatorCls}
        style={`left:${indicator.left}px;width:${indicator.width}px;opacity:${indicator.ready ? 1 : 0}`}
      />
      {items.map((item) => {
        if (item.hidden) return null;
        const isActive = item.id === value;
        const Icon = item.icon;
        // Cores do TEXTO do botão variam pelo size — no `default` o
        // indicador é primary (texto branco no ativo); no `sm` é accent
        // (texto foreground no ativo).
        // Texto ativo sempre em primary-foreground (branco no roxo). Strong
        // ainda fica negrito pra dar peso extra; subtle não-negrito pra ficar
        // mais leve em painéis cheios de controles (Preferências).
        const activeTextCls =
          variant === "strong"
            ? "text-primary-foreground font-bold"
            : "text-primary-foreground font-semibold";
        const inactiveTextCls =
          "text-muted-foreground hover:text-foreground";
        return (
          <button
            key={item.id}
            ref={(el) => {
              if (el) buttonRefs.current.set(item.id, el);
              else buttonRefs.current.delete(item.id);
            }}
            type="button"
            role="tab"
            onClick={() => !item.disabled && onChange(item.id)}
            title={item.title}
            disabled={item.disabled}
            class={cn(
              buttonBaseCls,
              isActive ? activeTextCls : inactiveTextCls,
              item.disabled && "cursor-not-allowed opacity-50"
            )}
          >
            {Icon && <Icon className={size === "sm" ? "size-3" : "size-4"} />}
            {item.shortLabel ? (
              <>
                <span class="hidden sm:inline">{item.label}</span>
                <span class="sm:hidden">{item.shortLabel}</span>
              </>
            ) : (
              item.label
            )}
          </button>
        );
      })}
    </div>
  );
}
